import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, today, fmtEur, fmtDate } from "@/lib/api";
import { PatientPicker } from "@/components/ApptDialog";

const TL = { bono: "Bono de terapia visual", syntonic: "Tratamiento Syntonic", tomatis: "Tratamiento Tomatis", wivi: "Tratamiento Wivi", sesiones: "Sesiones de terapia visual" };
const L = ({ t, children, className = "" }) => <label className={`block text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${className}`}>{t}<div className="mt-1">{children}</div></label>;

const Lineas = ({ lineas, setLineas }) => {
  const upd = (i, k, v) => setLineas(lineas.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-1.5" data-testid="factura-lineas">
      {lineas.map((x, i) => (
        <div key={i} className="grid grid-cols-[1fr_100px_90px_32px] gap-2">
          <Input value={x.concepto} onChange={(e) => upd(i, "concepto", e.target.value)} placeholder="Concepto" data-testid={`factura-linea-concepto-${i}`} />
          <Input type="number" step="0.01" value={x.importe} onChange={(e) => upd(i, "importe", e.target.value)} placeholder="Importe" data-testid={`factura-linea-importe-${i}`} />
          <select className="field-select" value={x.iva} onChange={(e) => upd(i, "iva", Number(e.target.value))} data-testid={`factura-linea-iva-${i}`}>
            <option value={0}>Exenta</option><option value={10}>IVA 10%</option><option value={21}>IVA 21%</option>
          </select>
          <button type="button" onClick={() => setLineas(lineas.filter((_, j) => j !== i))} className="text-rose-600" title="Quitar línea"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => setLineas([...lineas, { concepto: "", importe: "", iva: 0 }])} className="inline-flex items-center gap-1 text-xs text-[#1E3A8A]" data-testid="factura-add-linea"><Plus className="h-3.5 w-3.5" />Añadir línea</button>
    </div>
  );
};

const Cobros = ({ cobros, sel, toggle }) => (
  <div className="max-h-40 overflow-y-auto rounded border border-slate-200 divide-y" data-testid="factura-cobros">
    {cobros.length === 0 && <p className="p-2 text-xs text-slate-400">No tiene cobros sin facturar. Puedes escribir las líneas a mano.</p>}
    {cobros.map((c) => (
      <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
        <input type="checkbox" checked={sel.includes(c.id)} onChange={() => toggle(c)} data-testid={`factura-cobro-${c.id}`} />
        <span className="w-20 text-xs text-slate-500">{fmtDate(c.fecha)}</span>
        <span className="flex-1 truncate">{c.concepto || TL[c.tipo] || "Cobro"}</span>
        <span className="text-xs text-slate-500">{c.metodo}</span>
        <span className="font-[JetBrains_Mono] text-xs">{fmtEur(c.importe)}</span>
      </label>
    ))}
  </div>
);

export const FacturaDialog = ({ open, onClose, preset }) => {
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [cobros, setCobros] = useState([]);
  const [sel, setSel] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [f, setF] = useState({ fecha: today(), cliente_nombre: "", cliente_nif: "", cliente_direccion: "", notas: "" });
  useEffect(() => {
    if (open) { setP(null); setCobros([]); setSel([]); setLineas([]); setF({ fecha: today(), cliente_nombre: "", cliente_nif: "", cliente_direccion: "", notas: "" }); }
    if (open && preset) pick(preset.patient, preset.treatmentId);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (x, tid) => {
    setP(x); setSel([]); setLineas([]);
    if (!x) return;
    const [{ data: d }, { data: cs }] = await Promise.all([api.get(`/patients/${x.id}`), api.get("/eco/cobros", { params: { patient_id: x.id, desde: "2000-01-01", hasta: "2100-12-31" } })]);
    const pt = d.patient || d;
    const libres = cs.filter((c) => !c.factura);
    setCobros(libres);
    if (tid) {
      const mios = libres.filter((c) => c.treatment_id === tid);
      setSel(mios.map((c) => c.id));
      setLineas(mios.map((c) => ({ concepto: c.concepto ? `${TL[c.tipo] || ""} · ${c.concepto}` : TL[c.tipo] || "Servicio de optometría", importe: c.importe, iva: 0, cobro: c.id })));
    }
    setF((o) => ({ ...o, cliente_nombre: pt.tutor || pt.nombre_madre || pt.nombre_padre || pt.nombre, cliente_nif: pt.dni || "",
      cliente_direccion: [pt.direccion, pt.codigo_postal, pt.poblacion].filter(Boolean).join(", "), notas: `Paciente: ${pt.nombre}` }));
  };
  const toggle = (c) => {
    if (sel.includes(c.id)) {
      setSel(sel.filter((x) => x !== c.id));
      setLineas(lineas.filter((l) => l.cobro !== c.id));
    } else {
      setSel([...sel, c.id]);
      setLineas([...lineas, { concepto: c.concepto || TL[c.tipo] || "Servicio de optometría", importe: c.importe, iva: 0, cobro: c.id }]);
    }
  };
  const total = lineas.reduce((s, l) => s + Number(l.importe || 0) * (1 + l.iva / 100), 0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!window.confirm(`¿Emitir la factura por ${fmtEur(total)}? Una vez emitida no se puede borrar, solo rectificar.`)) return;
    try {
      const { data } = await api.post("/eco/facturas", { ...f, patient_id: p.id, cobro_ids: sel, lineas: lineas.map((l) => ({ concepto: l.concepto, importe: Number(l.importe), iva: l.iva })) });
      toast.success(`Factura ${data.numero} emitida`);
      nav(`/economico/factura/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="factura-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Nueva factura</DialogTitle></DialogHeader>
        <L t="Paciente"><PatientPicker value={p?.id} label={p?.nombre} onPick={pick} /></L>
        {p && <>
          <L t="Cobros sin facturar (marca los que entran)"><Cobros cobros={cobros} sel={sel} toggle={toggle} /></L>
          <div className="grid grid-cols-2 gap-3">
            <L t="A nombre de"><Input value={f.cliente_nombre} onChange={set("cliente_nombre")} data-testid="factura-cliente" /></L>
            <L t="NIF / DNI"><Input value={f.cliente_nif} onChange={set("cliente_nif")} data-testid="factura-nif" /></L>
            <L t="Dirección" className="col-span-2"><Input value={f.cliente_direccion} onChange={set("cliente_direccion")} data-testid="factura-direccion" /></L>
            <L t="Fecha"><Input type="date" value={f.fecha} onChange={set("fecha")} data-testid="factura-fecha" /></L>
            <L t="Nota en la factura"><Input value={f.notas} onChange={set("notas")} data-testid="factura-notas" /></L>
          </div>
          <L t="Líneas"><Lineas lineas={lineas} setLineas={setLineas} /></L>
        </>}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600" data-testid="factura-total">Total: <b>{fmtEur(total)}</b></span>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={!p || !lineas.length || !f.cliente_nombre || lineas.some((l) => !l.concepto || l.importe === "")} data-testid="factura-save" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Emitir factura</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
